import { calculateAge } from "../../../Utils/family-tree/dateAndTime";
import { formatDashboardFamilyLineHeadName } from "../../../Utils/dashboardMemberDisplayName";
import { getStateAbbreviation, normalizeStateName } from "../../../constants/states";

/** Same first-name + suffix/special-name rule the Events invite list uses. */
export const formatPersonName = (member) => {
  let firstName = member?.first_name || "";
  if (!firstName && member?.name) {
    firstName = member.name.trim().split(" ")[0] || "";
  }
  if (!firstName) firstName = "Unnamed";

  // Some member endpoints send the suffix as { name } rather than a string.
  const rawSuffix = member?.suffix;
  const suffix =
    (rawSuffix && typeof rawSuffix === "object" ? rawSuffix.name : rawSuffix) || "";
  const specialName = member?.special_name || "";

  if (suffix) {
    const suffixUpper = suffix.trim().toUpperCase();
    const suffixWithPeriod =
      suffixUpper === "SR" || suffixUpper === "JR"
        ? `${suffix.trim()}.`
        : suffix.trim();
    return `${firstName}, ${suffixWithPeriod}`;
  }
  if (specialName) return `${firstName} ${specialName}`;
  return firstName;
};

export const getHeadOfLineName = (member) =>
  formatDashboardFamilyLineHeadName(member);

/** Age from the API field when present, else derived from DOB. -1 when unknown. */
export const getMemberAge = (member) => {
  if (member?.age !== undefined && member?.age !== null) {
    const age = parseInt(member.age, 10);
    if (!Number.isNaN(age) && age >= 0) return age;
  }

  let dob = member?.dob || member?.date_of_birth || "";
  if (!dob) return -1;

  if (typeof dob === "number" || (typeof dob === "string" && /^-?\d+$/.test(dob))) {
    dob = new Date(parseInt(dob, 10)).toISOString();
  }

  const ageStr = calculateAge(dob);
  if (!ageStr) return -1;
  return parseInt(ageStr, 10) || -1;
};

/** Adults only — Host(s) is limited to family members 18 and older. */
export const isAdultMember = (member) => getMemberAge(member) >= 18;

/** The with-spouses endpoint returns each in-law as its own row, flagged
    type: "in_law" / is_in_laws: true. */
export const isInLawMember = (member) =>
  member?.type === "in_law" ||
  member?.is_in_laws === true ||
  member?.is_in_laws === "true" ||
  member?.is_in_laws === 1;

/* The member endpoints spell the residing state several ways and sometimes
   nest it, so read every shape rather than one. State of birth is deliberately
   not in the list — only where the member lives now filters the list. */
const RESIDING_STATE_KEYS = [
  "current_state_residing",
  "currentStateResiding",
  "current_state",
  "currentState",
  "state_residing",
  "stateResiding",
  "residing_state",
  "residingState",
  "state",
];

const RESIDING_STATE_CONTAINERS = [
  "user_details",
  "userDetails",
  "user",
  "member_details",
  "memberDetails",
  "details",
];

const readResidingState = (source) => {
  if (!source || typeof source !== "object") return "";
  for (const key of RESIDING_STATE_KEYS) {
    const value = source[key];
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

/** The residing state exactly as the record holds it, "" when there is none. */
export const getMemberStateRaw = (member) => {
  const direct = readResidingState(member);
  if (direct) return direct;
  for (const key of RESIDING_STATE_CONTAINERS) {
    const nested = readResidingState(member?.[key]);
    if (nested) return nested;
  }
  return "";
};

/** State the member currently resides in, as the canonical full name
 *  ("Georgia"). A value that is not one of the 50 states is kept as written so
 *  it still shows in the table and still filters. */
export const getMemberState = (member) => {
  const raw = getMemberStateRaw(member);
  if (!raw) return "";
  return normalizeStateName(raw) || raw;
};

/** Two-letter form shown in the table, e.g. "GA". "" when unknown. */
export const getMemberStateAbbr = (member) =>
  getStateAbbreviation(getMemberState(member));

export const getMemberKey = (member) =>
  member?.id || member?.user_id || member?.user?.user_id || "";

/** Trimmed-down member shape stored on a news entry. */
export const toPersonSummary = (member) => ({
  id: getMemberKey(member),
  // The Events invite table sends invitees by user_id, so attendees do too.
  user_id: member?.user_id || member?.user?.user_id || "",
  name: formatPersonName(member),
  head_of_line: getHeadOfLineName(member) || "Start/Top of Line",
  hex_code: member?.hex_code || "#000000",
  color_name: member?.color_name || "",
});

export const compareByName = (a, b) =>
  formatPersonName(a).toLowerCase().localeCompare(formatPersonName(b).toLowerCase());

/** Members with no state on file sort to the bottom of an ascending sort. */
export const compareByState = (a, b) => {
  const stateA = getMemberState(a);
  const stateB = getMemberState(b);
  if (!stateA && !stateB) return compareByName(a, b);
  if (!stateA) return 1;
  if (!stateB) return -1;
  if (stateA !== stateB) return stateA.localeCompare(stateB);
  return compareByName(a, b);
};

export const compareByFamilyLine = (a, b) => {
  const lineA = (a?.color_name || "").toLowerCase();
  const lineB = (b?.color_name || "").toLowerCase();
  if (lineA !== lineB) return lineA.localeCompare(lineB);
  const headA = (getHeadOfLineName(a) || "").toLowerCase();
  const headB = (getHeadOfLineName(b) || "").toLowerCase();
  if (headA !== headB) return headA.localeCompare(headB);
  return compareByName(a, b);
};
